'use client';

import React from 'react';
import { Search, X, SlidersHorizontal } from 'lucide-react';

interface Filters {
  search: string;
  category: string;
  brand: string;
  status: string;
  stockLevel: string;
}

interface ProductFiltersBarProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
}

const categories = [
  { value: '', label: 'All Categories' },
  { value: 'electronics', label: 'Electronics' },
  { value: 'mobile', label: 'Mobile & Tablets' },
  { value: 'laptops', label: 'Laptops & PCs' },
  { value: 'audio', label: 'Audio & Headphones' },
  { value: 'cameras', label: 'Cameras' },
  { value: 'accessories', label: 'Accessories' },
];

const brands = [
  { value: '', label: 'All Brands' },
  { value: 'samsung', label: 'Samsung' },
  { value: 'apple', label: 'Apple' },
  { value: 'sony', label: 'Sony' },
  { value: 'hp', label: 'HP' },
  { value: 'dell', label: 'Dell' },
  { value: 'haier', label: 'Haier' },
  { value: 'logitech', label: 'Logitech' },
  { value: 'xiaomi', label: 'Xiaomi' },
];

const statuses = [
  { value: '', label: 'All Status' },
  { value: 'published', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_review', label: 'Pending Review' },
  { value: 'rejected', label: 'Rejected' },
];

const stockLevels = [
  { value: '', label: 'All Stock' },
  { value: 'in_stock', label: 'In Stock (>10)' },
  { value: 'low_stock', label: 'Low Stock (1-10)' },
  { value: 'out_of_stock', label: 'Out of Stock' },
];

export default function ProductFiltersBar({ filters, onChange }: ProductFiltersBarProps) {
  const activeFilterCount = [
    filters.category,
    filters.brand,
    filters.status,
    filters.stockLevel,
  ].filter(Boolean).length;

  const clearAll = () => {
    onChange({ search: '', category: '', brand: '', status: '', stockLevel: '' });
  };

  return (
    <div className="card p-4">
      <div className="flex flex-wrap gap-3 items-center">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name, SKU, barcode..."
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            className="input-field pl-9 text-xs h-9"
          />
          {filters.search && (
            <button
              onClick={() => onChange({ ...filters, search: '' })}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Category */}
        <select
          value={filters.category}
          onChange={(e) => onChange({ ...filters, category: e.target.value })}
          className="input-field w-auto text-xs h-9 pr-8 cursor-pointer"
        >
          {categories.map((c) => (
            <option key={`cat-${c.value || 'all'}`} value={c.value}>{c.label}</option>
          ))}
        </select>

        {/* Brand */}
        <select
          value={filters.brand}
          onChange={(e) => onChange({ ...filters, brand: e.target.value })}
          className="input-field w-auto text-xs h-9 pr-8 cursor-pointer"
        >
          {brands.map((b) => (
            <option key={`brand-${b.value || 'all'}`} value={b.value}>{b.label}</option>
          ))}
        </select>

        {/* Status */}
        <select
          value={filters.status}
          onChange={(e) => onChange({ ...filters, status: e.target.value })}
          className="input-field w-auto text-xs h-9 pr-8 cursor-pointer"
        >
          {statuses.map((s) => (
            <option key={`status-${s.value || 'all'}`} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Stock level */}
        <select
          value={filters.stockLevel}
          onChange={(e) => onChange({ ...filters, stockLevel: e.target.value })}
          className="input-field w-auto text-xs h-9 pr-8 cursor-pointer"
        >
          {stockLevels.map((s) => (
            <option key={`stock-${s.value || 'all'}`} value={s.value}>{s.label}</option>
          ))}
        </select>

        {/* Clear filters */}
        {activeFilterCount > 0 && (
          <button
            onClick={clearAll}
            className="flex items-center gap-1.5 text-xs text-danger font-medium hover:underline"
          >
            <X size={13} />
            Clear ({activeFilterCount})
          </button>
        )}

        <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground">
          <SlidersHorizontal size={13} />
          <span>Showing 347 products</span>
        </div>
      </div>
    </div>
  );
}